import React from 'react';
import { themeColor } from '../utils/themeColor';
import './Footer.css';
import { useBrandIdentity } from '../utils/brandIdentity';

// Set to true to show RiskLo Watcher (desktop app) recommendation in footer
const SHOW_DESKTOP_APP_RECOMMENDATION = false;

function Footer() {
  const brand = useBrandIdentity();
  const handleDesktopAppDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Link to GitHub release (for large file hosting)
    // Update this URL after creating the GitHub release
    const releaseUrl = 'https://github.com/myleshumphrey/risklo/releases/latest/download/RiskLoWatcher.exe';
    
    // Try GitHub release first, fallback to local if available
    const link = document.createElement('a');
    link.href = releaseUrl;
    link.download = 'RiskLoWatcher.exe';
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDesktopAppGuide = () => {
    // Navigate to desktop app guide page
    window.dispatchEvent(new CustomEvent('navigate', { detail: 'desktopAppGuide' }));
  };

  return (
    <footer className="app-footer">
      <div className="footer-content">
        {/* Desktop App Section - Recommended (hidden when SHOW_DESKTOP_APP_RECOMMENDATION is false) */}
        {SHOW_DESKTOP_APP_RECOMMENDATION && (
          <div className="footer-addon-section">
            <div className="addon-info">
              <h3 className="addon-title">
                RiskLo Watcher (Desktop App) 
                <span className="recommended-badge">Recommended</span>
              </h3>
              <p className="addon-description">
                Simple Windows app that automatically uploads your NinjaTrader CSV exports to RiskLo. 
                Just export your CSVs and get instant email results - no website login needed!
              </p>
              <div className="addon-buttons">
                <button className="download-button primary" onClick={handleDesktopAppDownload}>
                  Download Desktop App
                </button>
                <button className="setup-link" onClick={handleDesktopAppGuide}>
                  User Guide
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="disclaimers">
          <div className="disclaimer-section">
            <h3 className="disclaimer-title">Financial Advice Disclaimer</h3>
            <p className="disclaimer-text">
              This tool is for informational and educational purposes only and does not constitute financial advice. 
              Users are solely responsible for their own trading decisions and the associated risks. We do not guarantee 
              the accuracy of the data or the outcomes of any analysis performed using this tool. Please consult with a 
              professional financial advisor before making any investment decisions.
            </p>
            <p className="disclaimer-text" style={{ marginTop: '1rem' }}>
              <strong>Read our full{' '}
              <a 
                href="/privacy-policy.html" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: themeColor('#667eea'), textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>
              {' '}and{' '}
              <a 
                href="/terms-and-conditions.html" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: themeColor('#667eea'), textDecoration: 'underline' }}
              >
                Terms & Conditions
              </a>
              .</strong>
            </p>
          </div>
          
          <div className="disclaimer-section">
            <h3 className="disclaimer-title">Affiliation Disclaimer</h3>
            <p className="disclaimer-text">
              {brand.isPlatform ? 'RiskLo is' : `${brand.appName} runs on RiskLo,`} an independent analytics tool that may display
              third-party strategy performance data made available to eligible users. RiskLo remains responsible for this
              tool&apos;s features, calculations, and outputs, and users remain responsible for their own trading decisions.
            </p>
          </div>
        </div>
        
        <div className="footer-trust-badges">
          <div className="trust-badge">
            <span className="trust-icon">✅</span>
            <span className="trust-text">Google OAuth Verification Completed</span>
          </div>
          <div className="trust-badge">
            <span className="trust-icon">🔒</span>
            <span className="trust-text">256-bit SSL Encryption</span>
          </div>
          <div className="trust-badge">
            <span className="trust-icon">📖</span>
            <span className="trust-text">Read-Only Access</span>
          </div>
          <div className="trust-badge">
            <span className="trust-icon">⚡</span>
            <span className="trust-text">Secure Data Handling</span>
          </div>
        </div>
        
        <div className="footer-copyright">
          <p>
            &copy; {new Date().getFullYear()} {brand.isPlatform ? 'Cultivate Dynamics LLC' : brand.legalName}. All rights reserved.
            {brand.isPlatform
              ? <span className="footer-powered-by"> RiskLo® is a registered trademark of Cultivate Dynamics LLC.</span>
              : <span className="footer-powered-by"> · Powered by RiskLo®</span>} | {' '}
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('navigate', { detail: 'privacy-policy' })); }}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'rgba(255, 255, 255, 0.6)', 
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit'
              }}
            >
              Privacy Policy
            </button>
            {' '} | {' '}
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('navigate', { detail: 'terms-and-conditions' })); }}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'rgba(255, 255, 255, 0.6)', 
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit'
              }}
            >
              Terms & Conditions
            </button>
          </p>
          <p style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            Contact us: {' '}
            <a 
              href="mailto:info@cultivatedynamics.com"
              style={{ 
                color: themeColor('#667eea'), 
                textDecoration: 'none'
              }}
            >
              info@cultivatedynamics.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;

